import { z } from 'zod';
import { somarDinheiro } from './aritmetica.ts';
import {
  dataDoFatoSchema,
  dataHoraSchema,
  dataSchema,
  dinheiroSchema,
  paginacaoSchema,
  respostaListaSchema,
  uuidSchema,
} from './comum.ts';

/**
 * Orçamento (`.spec/010`, "Orçamento"; `.spec/015`, decisão 58).
 *
 * É o **primeiro documento do processo comercial**, e essa posição é o que
 * explica quase tudo aqui: ele existe antes do contrato e antes da obra. A
 * construtora orça, o cliente aceita, e só então nasce o contrato — e a obra
 * depois dele. Um orçamento não referencia obra nenhuma porque, no instante em
 * que é escrito, ela não existe.
 *
 * Mestre e itens, como a solicitação de compra, e pelo mesmo motivo: orçamento
 * sem linha nenhuma é um documento que não orça nada, e deixá-lo existir entre
 * duas chamadas HTTP produziria exatamente essa linha.
 *
 * ## O que ele deliberadamente ainda não tem
 *
 * **Situação.** O pedido foi explícito em começar por aqui ("inicialmente"), e
 * inventar `APROVADO`/`RECUSADO`/`CONVERTIDO` agora seria escrever a semântica
 * antes de existir quem a escreva: nada no sistema converte orçamento em
 * contrato hoje, e uma coluna de situação que só o usuário move é um campo de
 * anotação disfarçado de máquina de estados. Ela entra junto com a conversão.
 *
 * **Quantidade no item.** O item do orçamento tem **valor**, e não
 * quantidade × preço. É como a proposta chega ao cliente — "Alvenaria de
 * vedação: R$ 84.000,00" —, e a decomposição em quantidade e unitário é a
 * composição de custo, que é outro assunto e outro documento.
 *
 * **Total gravado.** Ver `totalDoOrcamento`, abaixo.
 */

/* -------------------------------------------------------------------------- */
/* Itens                                                                      */
/* -------------------------------------------------------------------------- */

export const MAXIMO_ITENS_ORCAMENTO = 200;

/**
 * Valor da linha: **sempre positivo**.
 *
 * Zero não orça nada e ocuparia uma linha que o cliente leria para descobrir que
 * não há nada ali. Negativo seria desconto, que é outra coisa — desconto incide
 * sobre o total e tem regra própria, e escondê-lo como item de valor negativo
 * faria a soma fechar e o documento mentir sobre o que foi orçado.
 */
const valorDoItemSchema = dinheiroSchema.refine(
  (v) => Number(v) > 0,
  'O valor do item deve ser maior que zero.',
);

/**
 * Item no payload de gravação.
 *
 * **Não tem `numero`.** O número sequencial da linha é atribuído pelo servidor,
 * pela posição no array — a primeira é 1, a segunda é 2. Aceitá-lo do cliente
 * abriria as duas falhas que numeração digitada sempre abre: buraco (1, 2, 4) e
 * repetição (1, 2, 2), e a segunda só apareceria no índice único, como erro de
 * banco na cara de quem clicou em salvar.
 *
 * É a mesma escolha da parcela da condição de pagamento, e o efeito prático é o
 * que o usuário espera: arrastar uma linha para cima renumera as duas.
 *
 * **Não tem `id`.** Os itens são regravados por inteiro a cada `PUT`. Nada
 * aponta para o item de orçamento ainda — quando a conversão em contrato
 * existir, isto se reavalia.
 */
export const itemOrcamentoEntradaSchema = z.object({
  /** A linha do catálogo que esta posição orça. */
  itemOrcamentoId: uuidSchema,
  valor: valorDoItemSchema,
  observacao: z
    .string()
    .trim()
    .max(500, 'A observação do item tem no máximo 500 caracteres.')
    .optional(),
});

export type ItemDoOrcamentoEntrada = z.infer<typeof itemOrcamentoEntradaSchema>;

/**
 * O mesmo item do catálogo duas vezes no mesmo orçamento é digitação repetida.
 *
 * A soma das duas linhas é o que o orçamentista queria numa só, e mantê-las
 * separadas faz o cliente receber "Alvenaria" duas vezes com valores diferentes
 * e perguntar qual vale. O `UNIQUE (tenant_id, orcamento_id, item_orcamento_id)`
 * recusa no banco; aqui a recusa chega presa à linha, que é o que permite ao
 * formulário destacá-la.
 */
function validarItensDistintos(itens: ItemDoOrcamentoEntrada[], ctx: z.RefinementCtx): void {
  const vistos = new Set<string>();

  itens.forEach((item, indice) => {
    if (!vistos.has(item.itemOrcamentoId)) {
      vistos.add(item.itemOrcamentoId);
      return;
    }

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [indice, 'itemOrcamentoId'],
      message: 'Este item já está no orçamento. Some os valores numa linha só.',
    });
  });
}

const itensSchema = z
  .array(itemOrcamentoEntradaSchema)
  .min(1, 'O orçamento precisa de pelo menos um item.')
  .max(MAXIMO_ITENS_ORCAMENTO, `O orçamento tem no máximo ${MAXIMO_ITENS_ORCAMENTO} itens.`)
  .superRefine(validarItensDistintos);

/** Item como a API o devolve, com o catálogo já resolvido. */
export const itemDoOrcamentoSchema = z.object({
  id: uuidSchema,
  /** 1, 2, 3… na ordem do documento. Do servidor, nunca do payload. */
  numero: z.number().int(),
  itemOrcamentoId: uuidSchema,
  itemOrcamentoCodigo: z.string().nullable(),
  itemOrcamentoNome: z.string().nullable(),
  /** String decimal, nunca `number`: ponto flutuante perde centavo em silêncio. */
  valor: dinheiroSchema,
  observacao: z.string().nullable(),
});

export type ItemDoOrcamento = z.infer<typeof itemDoOrcamentoSchema>;

/* -------------------------------------------------------------------------- */
/* Documento                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Payload de gravação.
 *
 * **Sem `numero`**: quem o atribui é o contador da conta, na transação que cria
 * o orçamento. Aceitá-lo do cliente deixaria dois documentos nascerem com o
 * mesmo número, e o segundo descobriria isso no índice único.
 *
 * **Sem `valorTotal`**: ele é a soma dos itens, e chega junto com eles. Ver
 * `totalDoOrcamento`.
 */
export const criarOrcamentoSchema = z
  .object({
    /** Empresa do grupo que executa — `pessoas` com `is_empresa`. */
    empresaId: uuidSchema,
    /** Para quem se orça — `pessoas` com `is_cliente`. */
    clienteId: uuidSchema,
    /**
     * Dia do orçamento. Data de negócio, sem hora nem fuso.
     *
     * `dataDoFatoSchema`: o orçamento registra o que foi feito, e datá-lo no
     * futuro é a mesma contradição de toda data de fato do sistema
     * (`.spec/015`, decisão 34).
     */
    dataOrcamento: dataDoFatoSchema,
    /**
     * Até quando a construtora se compromete a entregar.
     *
     * `dataSchema`, e **não** `dataDoFatoSchema`: esta é a única data do
     * documento que **precisa** ser futura. Aplicar a trava do fato aqui
     * recusaria todo orçamento com prazo, que é todo orçamento.
     */
    dataPrazoEntrega: dataSchema,
    itens: itensSchema,
    observacao: z
      .string()
      .trim()
      .max(2000, 'A observação tem no máximo 2000 caracteres.')
      .optional(),
  })
  .superRefine((dados, ctx) => {
    /*
      **O cliente não pode ser a empresa executora.**

      Parece impossível de escolher e não é: `pessoas` é uma tabela só, e a mesma
      linha pode ter `is_empresa` e `is_cliente` — a construtora que presta
      serviço para outra do próprio grupo está cadastrada nos dois papéis, e os
      dois seletores da tela a oferecem. Orçar para si mesma não é documento
      nenhum.

      A regra entra **aqui, e não no service**, porque o banco já a cobra
      (`orcamentos_cliente_difere_empresa`) e o que faltava era alguém recusá-la
      antes: sem isto, a escolha chegava ao `INSERT` e voltava como violação de
      constraint — um 500 com texto de `CHECK` na cara de quem clicou em salvar.
      No contrato ela vale para a tela e para a API pelo mesmo código, e a recusa
      chega presa ao campo que o usuário precisa trocar.
    */
    if (dados.clienteId === dados.empresaId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['clienteId'],
        message: 'O cliente não pode ser a mesma pessoa da empresa executora.',
      });
    }

    if (dados.dataPrazoEntrega >= dados.dataOrcamento) return;

    /*
      Comparação de texto, e não de `Date`: as duas datas são `AAAA-MM-DD`, e
      nesse formato a ordem lexicográfica **é** a cronológica. Construir dois
      `Date` para comparar traria o fuso de volta a uma conta que não tem hora.
    */
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['dataPrazoEntrega'],
      message: 'O prazo de entrega não pode ser anterior à data do orçamento.',
    });
  });

export type CriarOrcamento = z.infer<typeof criarOrcamentoSchema>;
export const atualizarOrcamentoSchema = criarOrcamentoSchema;
export type AtualizarOrcamento = z.infer<typeof atualizarOrcamentoSchema>;

/**
 * Orçamento como a API o devolve.
 *
 * Os nomes de empresa e cliente vêm **desnormalizados**: a listagem mostra
 * "Construtora Aurora · Prefeitura de Criciúma", não dois uuid, e resolver isso
 * no cliente seriam duas consultas por linha.
 */
export const orcamentoSchema = z.object({
  id: uuidSchema,
  /** Sequencial da conta inteira, do servidor. É o número que se diz ao cliente. */
  numero: z.number().int(),
  empresaId: uuidSchema,
  empresaNome: z.string().nullable(),
  clienteId: uuidSchema,
  clienteNome: z.string().nullable(),
  dataOrcamento: dataSchema,
  dataPrazoEntrega: dataSchema,
  observacao: z.string().nullable(),
  /**
   * Os itens vêm **na listagem também**, não só no documento aberto.
   *
   * É o que permite à listagem mostrar o total sem uma segunda chamada por
   * linha, e é a mesma escolha da solicitação de compra. O custo é uma consulta
   * por página, não uma por documento.
   */
  itens: z.array(itemDoOrcamentoSchema),
  /**
   * O contrato e a obra que nasceram deste orçamento (`.spec/015`, decisão 59).
   *
   * Nulos enquanto nada foi gerado, que é o estado da maioria. O id e o rótulo
   * vêm juntos pelo mesmo motivo dos nomes de empresa e cliente: a tela mostra
   * "Contrato 2026/014", não um uuid, e resolvê-lo no cliente seria uma consulta
   * por linha da listagem.
   *
   * **A direção da coluna é a inversa desta leitura.** Quem guarda o vínculo é
   * `contratos.orcamento_id` e `obras.orcamento_id` — o documento gerado aponta
   * para a origem, como `obras.contrato_id` aponta para o contrato. Pôr as duas
   * colunas em `orcamentos` faria `schema/orcamentos.ts` importar
   * `schema/obras.ts`, que por sua vez precisa importar `orcamentos_itens` para
   * a parte da obra: um ciclo de módulos que quebra na inicialização do Drizzle.
   */
  contratoId: uuidSchema.nullable(),
  contratoNumero: z.string().nullable(),
  obraId: uuidSchema.nullable(),
  obraCodigo: z.string().nullable(),
  obraNome: z.string().nullable(),
  createdAt: dataHoraSchema,
  updatedAt: dataHoraSchema,
});

export type Orcamento = z.infer<typeof orcamentoSchema>;

/**
 * Já gerou alguma coisa? É a pergunta da **trava** (`.spec/015`, decisão 59).
 *
 * Função, e não coluna `gerado` nem `situacao`: o fato já está escrito nos dois
 * vínculos, e uma terceira coluna dizendo o mesmo é a que passa a discordar — um
 * `UPDATE` direto em qualquer um dos lados a deixaria mentindo.
 *
 * Mora no contrato porque as duas pontas perguntam: a tela decide se abre o
 * formulário em leitura, e o service recusa o `PUT` e o `DELETE` com a mesma
 * conta. Duas cópias divergiriam, e a divergência é formulário que deixa editar
 * e servidor que recusa.
 */
export function orcamentoGerado(orcamento: {
  contratoId: string | null;
  obraId: string | null;
}): boolean {
  return orcamento.contratoId !== null || orcamento.obraId !== null;
}

export const listaOrcamentosSchema = respostaListaSchema(orcamentoSchema);
export type ListaOrcamentos = z.infer<typeof listaOrcamentosSchema>;

/* -------------------------------------------------------------------------- */
/* Listagem                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Filtros.
 *
 * Sem `q`, como a solicitação de compra: quem procura um orçamento o procura
 * pelo **número**, que é o que está no papel em cima da mesa, ou pelo cliente.
 * Busca livre sobre observação devolve acerto que ninguém pediu.
 */
export const listarOrcamentosQuerySchema = paginacaoSchema.omit({ q: true }).extend({
  /** Exato: o número é curto, e "43" como "contém" traria 43, 143 e 430. */
  numero: z.coerce.number().int().min(1).optional(),
  empresaId: uuidSchema.optional(),
  clienteId: uuidSchema.optional(),
  /** Início do período da data do orçamento, inclusivo. */
  de: dataSchema.optional(),
  /** Fim do período da data do orçamento, inclusivo. */
  ate: dataSchema.optional(),
});

export type ListarOrcamentosQuery = z.infer<typeof listarOrcamentosQuerySchema>;

export const COLUNAS_ORDENAVEIS_ORCAMENTO = [
  'numero',
  'dataOrcamento',
  'dataPrazoEntrega',
  'createdAt',
] as const;

/* -------------------------------------------------------------------------- */
/* Regras compartilhadas entre a tela e a API                                 */
/* -------------------------------------------------------------------------- */

/**
 * O total do orçamento: a soma dos valores dos itens.
 *
 * **Não é coluna**, e a decisão é deliberada. A regra da casa — fato gravado não
 * se recalcula (ADR 0004) — protege o documento de ser reescrito por um
 * **cadastro** que mudou depois; não é o caso aqui. Este total é a soma dos
 * filhos do próprio documento, que são regravados por inteiro no mesmo `PUT`:
 * uma coluna guardaria o mesmo número por um caminho a mais, e o caminho a mais
 * é exatamente onde ele passa a discordar — basta um `UPDATE` direto numa linha
 * de item para o total gravado virar mentira que ninguém detecta.
 *
 * Mora no contrato, e não no service, porque a tela mostra o total enquanto o
 * usuário digita e o servidor devolve o mesmo número na resposta. Duas
 * implementações discordariam num arredondamento, e o total mudaria ao salvar.
 *
 * `somarDinheiro` soma sobre inteiros escalados (`BigInt`), sem ponto flutuante.
 */
export function totalDoOrcamento(itens: readonly { valor: string }[]): string {
  return somarDinheiro(itens.map((item) => item.valor));
}

/**
 * O resumo que a listagem mostra na coluna de itens.
 *
 * Dois casos, e o segundo é o que justifica a função existir: com um item só, o
 * nome dele diz mais do que "1 item" — e é o caso comum do orçamento de reforma.
 *
 * **`...DoOrcamento` no nome, e não `resumoDeItens`**: a solicitação de compra já
 * exporta uma função com esse nome, e o `export *` de `index.ts` torna as duas
 * ambíguas — o `tsc` recusa com TS2308. Quem chegou primeiro fica com o nome
 * curto; documento novo qualifica o seu. É a razão de `MAXIMO_ITENS_ORCAMENTO`
 * também carregar o sufixo.
 */
export function resumoDeItensDoOrcamento(itens: readonly ItemDoOrcamento[]): string {
  const primeiro = itens[0];
  if (!primeiro) return 'Sem itens';

  if (itens.length === 1) return primeiro.itemOrcamentoNome ?? '1 item';

  return `${primeiro.itemOrcamentoNome ?? 'Item'} e mais ${itens.length - 1}`;
}

/* -------------------------------------------------------------------------- */
/* Geração de contrato e obra                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Gerar contrato e/ou obra a partir do orçamento (`.spec/015`, decisão 59).
 *
 * É a passagem do comercial para a execução, e o ponto em que o orçamento deixa
 * de ser rascunho: o que foi orçado vira o contrato que se assina e a obra que
 * se toca, com as linhas da proposta virando as **partes da obra**.
 *
 * ## Por que um payload, e não um clique
 *
 * Contrato e obra exigem três coisas que o orçamento não tem — número do
 * contrato, código e nome da obra — e as três são identificadores que a
 * construtora já usa fora do sistema, impressos no contrato e na pasta. Inventar
 * um valor e gravar em silêncio criaria cadastro com código que ninguém
 * reconhece, e obrigaria a corrigir dois cadastros logo depois de gerar.
 *
 * A tela sugere os três (ver as funções de sugestão abaixo) e deixa editar: é a
 * mesma escolha do diálogo de gerar financeiro do contrato, onde a data base vem
 * preenchida e muda.
 *
 * ## O que **não** está aqui, e de onde vem
 *
 * - **Empresa e cliente** — são os do orçamento. Perguntá-los de novo abriria a
 *   chance de gerar um contrato para outro cliente que não o orçado, que é
 *   exatamente o que o vínculo existe para impedir.
 * - **Tipo do contrato** — é sempre `RECEBIMENTO`. A construtora orça o que ela
 *   executa e recebe; um orçamento de onde saísse contrato de pagamento seria a
 *   construtora orçando para si mesma, que o `CHECK` de cliente ≠ empresa já
 *   recusa na origem.
 * - **Valor** — é `totalDoOrcamento`, a soma das linhas. Digitá-lo abriria a
 *   diferença entre a proposta enviada e o contrato assinado sem nada registrar
 *   que houve desconto.
 * - **Conta contábil e condição de pagamento** — ficam nulas nos dois. Elas
 *   pertencem ao ato de **gerar título**, que continua sendo outro ato, com sua
 *   própria tela e sua própria data base.
 * - **Endereço da obra** — nulo. O orçamento não tem endereço, e inventar um a
 *   partir do cadastro do cliente poria o endereço de cobrança no lugar do
 *   canteiro.
 */

export const dadosDoContratoGeradoSchema = z.object({
  /** Número do documento assinado. Único na conta — ver `criarContratoSchema`. */
  numero: z
    .string()
    .trim()
    .min(1, 'Informe o número do contrato.')
    .max(60, 'O número do contrato tem no máximo 60 caracteres.'),
  /**
   * Data de assinatura, e **não** a data do orçamento.
   *
   * A queda da tela é hoje. Copiar a data do orçamento afirmaria uma assinatura
   * que não aconteceu naquele dia — e a diferença entre as duas datas é
   * justamente o tempo que o cliente levou para aceitar, que é informação.
   */
  dataContrato: dataDoFatoSchema,
  /** Objeto do contrato. A tela sugere a partir do orçamento; ver abaixo. */
  descricao: z
    .string()
    .trim()
    .min(2, 'Descreva o objeto do contrato.')
    .max(2000, 'A descrição tem no máximo 2000 caracteres.'),
});

export type DadosDoContratoGerado = z.infer<typeof dadosDoContratoGeradoSchema>;

export const dadosDaObraGeradaSchema = z.object({
  /** Único na conta, até 30 caracteres — vira o número do documento dos títulos. */
  codigo: z
    .string()
    .trim()
    .min(1, 'Informe o código da obra.')
    .max(30, 'O código da obra tem no máximo 30 caracteres.'),
  /** Único na conta, e é por ele que a obra aparece em todo combo de rateio. */
  nome: z
    .string()
    .trim()
    .min(1, 'Informe o nome da obra.')
    .max(200, 'O nome da obra tem no máximo 200 caracteres.'),
  descricao: z.string().trim().max(2000, 'A descrição tem no máximo 2000 caracteres.').optional(),
});

export type DadosDaObraGerada = z.infer<typeof dadosDaObraGeradaSchema>;

/**
 * O payload: contrato, obra, ou os dois — **nunca nenhum**.
 *
 * Os dois nulos é chamada sem efeito, e responder 200 a ela faria a tela
 * declarar sucesso sobre nada. A recusa é do contrato, e não do service, porque
 * é validação de forma do payload: nada no banco precisa ser consultado para
 * saber que não há o que fazer.
 *
 * **Gerar os dois é um ato só, numa transação só**, e a obra nasce já apontando
 * para o contrato. Gerar em duas chamadas deixaria a janela em que o contrato
 * existe e a obra não — e quem tentasse de novo esbarraria no contrato já
 * gerado, sem caminho para criar só a obra que faltou.
 */
export const gerarDoOrcamentoSchema = z
  .object({
    contrato: dadosDoContratoGeradoSchema.nullable().prefault(null),
    obra: dadosDaObraGeradaSchema.nullable().prefault(null),
  })
  .superRefine((dados, ctx) => {
    if (dados.contrato !== null || dados.obra !== null) return;

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['contrato'],
      message: 'Escolha ao menos um: gerar o contrato, a obra, ou os dois.',
    });
  });

export type GerarDoOrcamento = z.infer<typeof gerarDoOrcamentoSchema>;

/* -------------------------------------------------------------------------- */
/* Sugestões da tela de geração                                               */
/* -------------------------------------------------------------------------- */

/**
 * As sugestões moram no contrato, e não no componente do diálogo.
 *
 * Três motivos, e o terceiro é o que decide: a documentação da rota cita o que a
 * tela sugere; o teste do contrato as cobre sem montar React; e no dia em que a
 * geração ganhar uma segunda porta — importação em lote, API de parceiro — ela
 * chega ao mesmo texto sem copiar a regra.
 *
 * Todas são **sugestões**: o usuário edita as três antes de confirmar, e nenhuma
 * delas é regra que o servidor cobre.
 */

/** "ORC-87" — o número do orçamento é o fio que liga os três documentos. */
export function numeroSugeridoDoContrato(numeroDoOrcamento: number): string {
  return `ORC-${numeroDoOrcamento}`;
}

/** O mesmo fio, e cabe nos 30 caracteres do número de documento do título. */
export function codigoSugeridoDaObra(numeroDoOrcamento: number): string {
  return `ORC-${numeroDoOrcamento}`;
}

/**
 * "Prefeitura de Criciúma — orçamento 87".
 *
 * O nome da obra é único na conta e é o que aparece em todo combo de rateio, e
 * por isso ele leva o cliente **e** o número: duas obras do mesmo cliente são o
 * caso comum, e "Prefeitura de Criciúma" sozinho colidiria na segunda.
 *
 * Cortado em 200 caracteres, que é o teto da coluna: um nome de cliente longo
 * faria o servidor recusar uma sugestão que a própria tela escreveu.
 */
export function nomeSugeridoDaObra(orcamento: {
  numero: number;
  clienteNome: string | null;
}): string {
  const sufixo = ` — orçamento ${orcamento.numero}`;
  const cliente = orcamento.clienteNome ?? 'Cliente';

  return `${cliente.slice(0, 200 - sufixo.length)}${sufixo}`;
}

/** "Execução do orçamento 87, de 19/09/2026." — o objeto, até alguém reescrevê-lo. */
export function descricaoSugeridaDoContrato(orcamento: {
  numero: number;
  dataOrcamento: string;
}): string {
  const [ano, mes, dia] = orcamento.dataOrcamento.split('-');

  return `Execução do orçamento ${orcamento.numero}, de ${dia}/${mes}/${ano}.`;
}
